import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Card, CardBody, Input, Label, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import FeatherIcon from "feather-icons-react";

import {
  getAutomatedEmailSettings,
  getIncidentRecipientOptions,
  saveAutomatedEmailSettings,
  sendAutomatedEmailTest,
} from "./api";
import "./automated-emails.scss";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const ALL_SECTIONS = [
  "inspectionReminders",
  "inspectionWeeklyDigest",
  "inspectionMonthlyReport",
  "maintenanceDigest",
  "occupancyReport",
  "buildingInspectionIssues",
  "incidentReports",
];

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// Toggle chips read Mon..Sun even though the stored value is 0=Sun..6=Sat.
const DIGEST_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const REMINDER_TIERS = [
  { value: "heads_up", label: "Heads-up" },
  { value: "reminder", label: "Reminder" },
  { value: "action", label: "Action needed" },
  { value: "urgent", label: "Urgent" },
  { value: "due_today", label: "Due today" },
  { value: "overdue", label: "Overdue" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MILESTONE_MIN = 1;
const MILESTONE_MAX = 365;
const REPEAT_MIN = 1;
const REPEAT_MAX = 60;
const STALE_MIN = 1;
const STALE_MAX = 8760;

const DEFAULTS = {
  inspectionReminders: {
    enabled: true,
    milestones: [60, 45, 30],
    repeatEveryDays: 5,
    includeCompany: false,
    extraRecipients: [],
  },
  inspectionWeeklyDigest: { enabled: true, dayOfWeek: 1, extraRecipients: [] },
  inspectionMonthlyReport: { enabled: true, extraRecipients: [] },
  maintenanceDigest: {
    enabled: true,
    days: [1, 5],
    staleAfterHours: 72,
    extraRecipients: [],
  },
  occupancyReport: { enabled: true, dayOfWeek: 5, extraRecipients: [] },
  buildingInspectionIssues: { enabled: true, days: [1, 3], extraRecipients: [] },
  incidentReports: {
    customRecipients: false,
    includeCompany: true,
    managerIds: [],
    extraRecipients: [],
  },
};

const SECTION_META = {
  inspectionReminders: {
    title: "Inspection reminders",
    description:
      "Countdown emails before each inspection is due, then repeat reminders while it stays overdue.",
    note:
      "Sent to the building's managers, its technical staff and every technical-staff lead. Add any other mailbox below.",
    testKind: "inspection_reminder",
  },
  inspectionWeeklyDigest: {
    title: "Weekly inspection digest",
    description:
      "A once-a-week summary of the inspections coming up and the ones already overdue.",
    note:
      "Goes to the company login and the extra addresses below (a marketing mailbox, for example).",
    testKind: "inspection_weekly",
  },
  inspectionMonthlyReport: {
    title: "Monthly inspection report",
    description:
      "A month-end report of every inspection completed, outstanding and overdue.",
    note:
      "Sent on the last day of every month to the company login, all technical-staff leads, every manager (their own properties) and the extra addresses below.",
    testKind: "inspection_monthly",
  },
  maintenanceDigest: {
    title: "Maintenance digest",
    description:
      "A snapshot of open maintenance tickets, flagging the ones that have gone quiet.",
    note:
      "Goes to everyone in the company: company login, managers and technical staff.",
    testKind: "maintenance_digest",
  },
  occupancyReport: {
    title: "Occupancy report",
    description:
      "Weekly occupancy by building, delivered alongside the maintenance update.",
    note:
      "Occupancy by building with the maintenance update, to everyone in the company.",
    testKind: "occupancy_report",
  },
  buildingInspectionIssues: {
    title: "Building inspection issues",
    description:
      "Items flagged as needing work on each building's latest inspection and not yet marked Done.",
    note:
      "The company login and the extra addresses below see every building; managers and technical-staff leads see their own. Nothing is sent once every item is Done.",
    testKind: "building_inspection_issues",
  },
  incidentReports: {
    title: "Incident Reports — who receives them",
    description:
      "When an incident report is submitted, created, updated or its investigation is completed, these people are emailed.",
    note: "",
    testKind: null,
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const toInt = (value, fallback) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const uniqSortedDesc = (list) =>
  Array.from(new Set(list)).sort((a, b) => b - a);

const uniqSortedAsc = (list) =>
  Array.from(new Set(list)).sort((a, b) => a - b);

const cleanMilestones = (list) =>
  uniqSortedDesc(
    (Array.isArray(list) ? list : [])
      .map((n) => toInt(n, NaN))
      .filter((n) => Number.isFinite(n) && n >= MILESTONE_MIN && n <= MILESTONE_MAX)
  );

const cleanDays = (list) =>
  uniqSortedAsc(
    (Array.isArray(list) ? list : [])
      .map((n) => toInt(n, NaN))
      .filter((n) => Number.isFinite(n) && n >= 0 && n <= 6)
  );

const cleanRecipients = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((r) => r && typeof r.email === "string" && r.email.trim())
    .map((r) => ({
      name: String(r.name || "").trim(),
      email: r.email.trim(),
    }));

const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

const cleanIds = (list) =>
  Array.from(
    new Set(
      (Array.isArray(list) ? list : [])
        .map((id) => String(id || "").trim().toLowerCase())
        .filter((id) => OBJECT_ID_RE.test(id))
    )
  );

// Same rule as the server: "Active" in any case, or no status at all.
const isActiveStatus = (status) =>
  !status || String(status).trim().toLowerCase() === "active";

const cleanDayOfWeek = (value, fallback) => {
  const n = toInt(value, fallback);
  return n >= 0 && n <= 6 ? n : fallback;
};

/**
 * Fill in anything the server left out so every section renders safely,
 * and coerce numbers so dirty-checking compares like with like.
 */
export const normalizeSettings = (raw) => {
  const src = raw && typeof raw === "object" ? raw : {};
  const rem = src.inspectionReminders || {};
  const weekly = src.inspectionWeeklyDigest || {};
  const monthly = src.inspectionMonthlyReport || {};
  const maint = src.maintenanceDigest || {};
  const occ = src.occupancyReport || {};
  const issues = src.buildingInspectionIssues || {};
  const incidents = src.incidentReports || {};

  return {
    ...src,
    inspectionReminders: {
      enabled: rem.enabled ?? DEFAULTS.inspectionReminders.enabled,
      milestones: cleanMilestones(
        rem.milestones ?? DEFAULTS.inspectionReminders.milestones
      ),
      repeatEveryDays: clamp(
        toInt(rem.repeatEveryDays, DEFAULTS.inspectionReminders.repeatEveryDays),
        REPEAT_MIN,
        REPEAT_MAX
      ),
      includeCompany: !!(
        rem.includeCompany ?? DEFAULTS.inspectionReminders.includeCompany
      ),
      extraRecipients: cleanRecipients(rem.extraRecipients),
    },
    inspectionWeeklyDigest: {
      enabled: weekly.enabled ?? DEFAULTS.inspectionWeeklyDigest.enabled,
      dayOfWeek: cleanDayOfWeek(
        weekly.dayOfWeek,
        DEFAULTS.inspectionWeeklyDigest.dayOfWeek
      ),
      extraRecipients: cleanRecipients(weekly.extraRecipients),
    },
    inspectionMonthlyReport: {
      enabled: monthly.enabled ?? DEFAULTS.inspectionMonthlyReport.enabled,
      extraRecipients: cleanRecipients(monthly.extraRecipients),
    },
    maintenanceDigest: {
      enabled: maint.enabled ?? DEFAULTS.maintenanceDigest.enabled,
      days: cleanDays(maint.days ?? DEFAULTS.maintenanceDigest.days),
      staleAfterHours: clamp(
        toInt(maint.staleAfterHours, DEFAULTS.maintenanceDigest.staleAfterHours),
        STALE_MIN,
        STALE_MAX
      ),
      extraRecipients: cleanRecipients(maint.extraRecipients),
    },
    occupancyReport: {
      enabled: occ.enabled ?? DEFAULTS.occupancyReport.enabled,
      dayOfWeek: cleanDayOfWeek(occ.dayOfWeek, DEFAULTS.occupancyReport.dayOfWeek),
      extraRecipients: cleanRecipients(occ.extraRecipients),
    },
    buildingInspectionIssues: {
      enabled: issues.enabled ?? DEFAULTS.buildingInspectionIssues.enabled,
      days: cleanDays(issues.days ?? DEFAULTS.buildingInspectionIssues.days),
      extraRecipients: cleanRecipients(issues.extraRecipients),
    },
    incidentReports: {
      customRecipients: incidents.customRecipients === true,
      includeCompany: incidents.includeCompany !== false,
      managerIds: cleanIds(incidents.managerIds),
      extraRecipients: cleanRecipients(incidents.extraRecipients),
    },
  };
};

const pickSections = (settings, keys) =>
  keys.reduce((acc, key) => {
    if (settings && settings[key]) acc[key] = settings[key];
    return acc;
  }, {});

/**
 * Same ladder the server builds: the milestones (descending), then counting
 * down from the smallest milestone by repeatEveryDays until the due date.
 * e.g. [60,45,30] every 5 -> 60,45,30,25,20,15,10,5,0
 */
export const computeLadder = (milestones, repeatEveryDays) => {
  const ms = cleanMilestones(milestones);
  const every = clamp(toInt(repeatEveryDays, DEFAULTS.inspectionReminders.repeatEveryDays), REPEAT_MIN, REPEAT_MAX);
  const steps = [...ms];
  if (ms.length) {
    for (let d = ms[ms.length - 1] - every; d > 0; d -= every) {
      steps.push(d);
    }
  }
  steps.push(0);

  const before = steps.filter((s) => s > 0);
  const everyText = every === 1 ? "every day" : `every ${every} days`;
  const sentence = before.length
    ? `${before.join(", ")} days before the due date, on the day, then ${everyText} while overdue.`
    : `On the day, then ${everyText} while overdue.`;

  return { steps, sentence, milestones: ms, every };
};

const pillTone = (step, ms) => {
  if (step === 0) return "is-red";
  if (!ms.length) return "is-orange";
  if (step >= ms[0]) return "is-blue";
  if (ms.length > 1 && step >= ms[1]) return "is-teal";
  if (step >= ms[ms.length - 1]) return "is-amber";
  return "is-orange";
};

const LADDER_LEGEND = [
  { tone: "is-blue", color: "#1a6fb5", label: "First milestone" },
  { tone: "is-teal", color: "#0d9488", label: "Second milestone" },
  { tone: "is-amber", color: "#d97706", label: "Last milestone" },
  { tone: "is-orange", color: "#ea580c", label: "Repeat reminders" },
  { tone: "is-red", color: "#dc2626", label: "Due date" },
];

const formatTimestamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const RecipientsEditor = ({ idPrefix, recipients, onChange, readOnly }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const add = () => {
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) {
      toast.error("Enter a valid email address.");
      return;
    }
    const exists = recipients.some(
      (r) => r.email.toLowerCase() === trimmedEmail.toLowerCase()
    );
    if (exists) {
      toast.error(`${trimmedEmail} is already on the list.`);
      return;
    }
    onChange([...recipients, { name: trimmedName, email: trimmedEmail }]);
    setName("");
    setEmail("");
  };

  const remove = (index) => {
    onChange(recipients.filter((_, i) => i !== index));
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      add();
    }
  };

  return (
    <div className="ae-recipients">
      <span className="ae-label" id={`${idPrefix}-recipients-label`}>
        Extra recipients
      </span>

      {recipients.length === 0 ? (
        <p className="ae-empty mb-2">No extra recipients.</p>
      ) : (
        <ul
          className="ae-recipient-list"
          aria-labelledby={`${idPrefix}-recipients-label`}
        >
          {recipients.map((r, index) => (
            <li key={`${r.email}-${index}`} className="ae-recipient-row">
              <div className="ae-recipient-text">
                {r.name ? (
                  <span className="ae-recipient-name">{r.name}</span>
                ) : null}
                <span className="ae-recipient-email">{r.email}</span>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  className="ae-icon-btn"
                  aria-label={`Remove ${r.email}`}
                  title="Remove"
                  onClick={() => remove(index)}
                >
                  <FeatherIcon icon="x" size={16} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div className="ae-inline-form">
          <div>
            <Label for={`${idPrefix}-recipient-name`} className="ae-label">
              Name
            </Label>
            <Input
              id={`${idPrefix}-recipient-name`}
              type="text"
              value={name}
              placeholder="Optional"
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={onKeyDown}
            />
          </div>
          <div>
            <Label for={`${idPrefix}-recipient-email`} className="ae-label">
              Email
            </Label>
            <Input
              id={`${idPrefix}-recipient-email`}
              type="email"
              value={email}
              placeholder="name@example.com"
              autoComplete="off"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKeyDown}
            />
          </div>
          <Button
            type="button"
            outline
            color="primary"
            className="ae-btn"
            onClick={add}
          >
            <FeatherIcon icon="plus" size={14} />
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

RecipientsEditor.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  recipients: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, email: PropTypes.string })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

const InspectionRemindersFields = ({ idPrefix, value, onChange, readOnly }) => {
  const [newMilestone, setNewMilestone] = useState("");

  const ladder = useMemo(
    () => computeLadder(value.milestones, value.repeatEveryDays),
    [value.milestones, value.repeatEveryDays]
  );

  const addMilestone = () => {
    const n = toInt(newMilestone, NaN);
    if (!Number.isFinite(n) || n < MILESTONE_MIN || n > MILESTONE_MAX) {
      toast.error(`Enter a number of days between ${MILESTONE_MIN} and ${MILESTONE_MAX}.`);
      return;
    }
    if (value.milestones.includes(n)) {
      toast.error(`${n} days is already a milestone.`);
      return;
    }
    onChange({ milestones: uniqSortedDesc([...value.milestones, n]) });
    setNewMilestone("");
  };

  const removeMilestone = (n) => {
    onChange({ milestones: value.milestones.filter((m) => m !== n) });
  };

  const onMilestoneKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addMilestone();
    }
  };

  const commitRepeat = () => {
    onChange({
      repeatEveryDays: clamp(
        toInt(value.repeatEveryDays, DEFAULTS.inspectionReminders.repeatEveryDays),
        REPEAT_MIN,
        REPEAT_MAX
      ),
    });
  };

  return (
    <>
      <div>
        <Label for={`${idPrefix}-milestone`} className="ae-label">
          Reminder milestones (days before the due date)
        </Label>
        <div className="ae-chips mb-2">
          {value.milestones.length === 0 ? (
            <p className="ae-empty">No milestones yet. Add one below.</p>
          ) : (
            value.milestones.map((m) => (
              <span key={m} className="ae-chip">
                {m} {m === 1 ? "day" : "days"}
                {!readOnly && (
                  <button
                    type="button"
                    className="ae-chip-remove"
                    aria-label={`Remove the ${m}-day milestone`}
                    title="Remove"
                    onClick={() => removeMilestone(m)}
                  >
                    <FeatherIcon icon="x" size={14} />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        {!readOnly && (
          <div className="ae-inline-form">
            <Input
              id={`${idPrefix}-milestone`}
              type="number"
              min={MILESTONE_MIN}
              max={MILESTONE_MAX}
              className="ae-number"
              value={newMilestone}
              placeholder="e.g. 14"
              onChange={(e) => setNewMilestone(e.target.value)}
              onKeyDown={onMilestoneKeyDown}
            />
            <Button
              type="button"
              outline
              color="primary"
              className="ae-btn"
              onClick={addMilestone}
            >
              <FeatherIcon icon="plus" size={14} />
              Add
            </Button>
          </div>
        )}
      </div>

      <div className="ae-field-row">
        <div>
          <Label for={`${idPrefix}-repeat`} className="ae-label">
            Then every
          </Label>
          <div className="ae-input-suffix">
            <Input
              id={`${idPrefix}-repeat`}
              type="number"
              min={REPEAT_MIN}
              max={REPEAT_MAX}
              className="ae-number"
              value={value.repeatEveryDays}
              disabled={readOnly}
              onChange={(e) =>
                onChange({
                  repeatEveryDays:
                    e.target.value === "" ? "" : toInt(e.target.value, ""),
                })
              }
              onBlur={commitRepeat}
            />
            <span>days</span>
          </div>
        </div>

        <div className="form-check ae-check">
          <input
            className="form-check-input"
            type="checkbox"
            id={`${idPrefix}-include-company`}
            checked={!!value.includeCompany}
            disabled={readOnly}
            onChange={(e) => onChange({ includeCompany: e.target.checked })}
          />
          <label
            className="form-check-label"
            htmlFor={`${idPrefix}-include-company`}
          >
            Also send to the company login
          </label>
        </div>
      </div>

      <div className="ae-ladder" aria-live="polite">
        <p className="ae-ladder-heading">Reminder schedule</p>
        <p className="ae-ladder-sentence">{ladder.sentence}</p>
        <div className="ae-pills" aria-label="Reminder steps in days before the due date">
          {ladder.steps.map((step) => (
            <span
              key={step}
              className={`ae-pill ${pillTone(step, ladder.milestones)}`}
              title={step === 0 ? "On the due date" : `${step} days before`}
            >
              {step === 0 ? "Due" : step}
            </span>
          ))}
        </div>
        <ul className="ae-ladder-legend">
          {LADDER_LEGEND.map((item) => (
            <li key={item.tone}>
              <span
                className="ae-legend-dot"
                style={{ backgroundColor: item.color }}
                aria-hidden="true"
              />
              {item.label}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

InspectionRemindersFields.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

const WeekdayField = ({ id, label, value, onChange, readOnly }) => (
  <div>
    <Label for={id} className="ae-label">
      {label}
    </Label>
    <Input
      id={id}
      type="select"
      className="ae-select"
      value={value}
      disabled={readOnly}
      onChange={(e) => onChange(cleanDayOfWeek(e.target.value, value))}
    >
      {WEEKDAYS.map((day, index) => (
        <option key={day} value={index}>
          {day}
        </option>
      ))}
    </Input>
  </div>
);

WeekdayField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

const DigestDaysField = ({ idPrefix, days, onChange, readOnly }) => {
  const toggleDay = (day) => {
    const next = days.includes(day)
      ? days.filter((d) => d !== day)
      : [...days, day];
    onChange(uniqSortedAsc(next));
  };

  return (
    <div>
      <span className="ae-label" id={`${idPrefix}-days-label`}>
        Send on
      </span>
      <div
        className="ae-chips"
        role="group"
        aria-labelledby={`${idPrefix}-days-label`}
      >
        {DIGEST_DAY_ORDER.map((day) => (
          <button
            key={day}
            type="button"
            className="ae-toggle"
            aria-pressed={days.includes(day)}
            aria-label={WEEKDAYS[day]}
            disabled={readOnly}
            onClick={() => toggleDay(day)}
          >
            {SHORT_DAYS[day]}
          </button>
        ))}
      </div>
      {days.length === 0 && (
        <p className="ae-help">
          Pick at least one day, otherwise nothing goes out.
        </p>
      )}
    </div>
  );
};

DigestDaysField.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  days: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

const MaintenanceDigestFields = ({ idPrefix, value, onChange, readOnly }) => {
  const commitStale = () => {
    onChange({
      staleAfterHours: clamp(
        toInt(value.staleAfterHours, DEFAULTS.maintenanceDigest.staleAfterHours),
        STALE_MIN,
        STALE_MAX
      ),
    });
  };

  return (
    <>
      <DigestDaysField
        idPrefix={idPrefix}
        days={value.days}
        readOnly={readOnly}
        onChange={(days) => onChange({ days })}
      />

      <div>
        <Label for={`${idPrefix}-stale`} className="ae-label">
          Needs attention after
        </Label>
        <div className="ae-input-suffix">
          <Input
            id={`${idPrefix}-stale`}
            type="number"
            min={STALE_MIN}
            max={STALE_MAX}
            className="ae-number"
            value={value.staleAfterHours}
            disabled={readOnly}
            onChange={(e) =>
              onChange({
                staleAfterHours:
                  e.target.value === "" ? "" : toInt(e.target.value, ""),
              })
            }
            onBlur={commitStale}
          />
          <span>hours</span>
        </div>
        <p className="ae-help">
          Open tickets older than this are listed as needing attention.
        </p>
      </div>
    </>
  );
};

MaintenanceDigestFields.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

const recipientPropType = PropTypes.shape({ name: PropTypes.string, email: PropTypes.string });

/**
 * Who receives the incident report emails meant for the property team.
 * Emails to the people involved, and addresses typed on a report, always go out.
 */
const IncidentReportRecipientsFields = ({ idPrefix, value, onChange, readOnly, options, optionsError }) => {
  const [filter, setFilter] = useState("");
  const managers = options?.managers || [];
  const companyEmail = options?.company?.email || "";
  const selected = new Set(value.managerIds);

  const visibleManagers = managers.filter((m) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return `${m.name} ${m.email}`.toLowerCase().includes(q);
  });

  const toggleManager = (id, checked) => {
    const next = checked
      ? [...value.managerIds.filter((x) => x !== id), id]
      : value.managerIds.filter((x) => x !== id);
    onChange({ managerIds: next });
  };

  const summary = (() => {
    if (!value.customRecipients) {
      return "Reports go to the property managers and lead technicians of the property the report is for.";
    }
    const byId = new Map(managers.map((m) => [m._id, m]));
    const names = [];
    if (value.includeCompany) names.push(companyEmail ? `the company account (${companyEmail})` : "the company account");
    if (!options && value.managerIds.length) {
      const n = value.managerIds.length;
      names.push(`${n} chosen manager${n === 1 ? "" : "s"}`);
    } else {
      value.managerIds.forEach((id) => {
        const m = byId.get(id);
        if (m && isActiveStatus(m.status)) names.push(m.name);
      });
    }
    value.extraRecipients.forEach((r) => names.push(r.name ? `${r.name} (${r.email})` : r.email));
    if (!names.length) return "";
    return `Reports go to ${names.join(", ")} — for every property.`;
  })();

  return (
    <>
      <div className="form-check form-switch ae-switch">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id={`${idPrefix}-custom`}
          checked={!!value.customRecipients}
          disabled={readOnly}
          onChange={(e) => onChange({ customRecipients: e.target.checked })}
        />
        <label className="form-check-label" htmlFor={`${idPrefix}-custom`}>
          Send to chosen people only
        </label>
      </div>

      {!value.customRecipients ? (
        <p className="ae-help">
          Off: property managers and lead technicians of the property (default).
        </p>
      ) : (
        <>
          <div className="form-check ae-check">
            <input
              className="form-check-input"
              type="checkbox"
              id={`${idPrefix}-include-company`}
              checked={!!value.includeCompany}
              disabled={readOnly}
              onChange={(e) => onChange({ includeCompany: e.target.checked })}
            />
            <label className="form-check-label" htmlFor={`${idPrefix}-include-company`}>
              Company account{companyEmail ? ` (${companyEmail})` : ""}
            </label>
          </div>

          <div>
            <span className="ae-label" id={`${idPrefix}-managers-label`}>
              Managers
            </span>
            {optionsError && <p className="ae-off-notice">{optionsError}</p>}
            {!optionsError && !options && (
              <p className="ae-empty">
                <Spinner size="sm" /> Loading managers...
              </p>
            )}
            {options && managers.length > 6 && (
              <Input
                type="search"
                className="mb-2"
                placeholder="Filter managers"
                aria-label="Filter managers"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            )}
            {options && managers.length === 0 && <p className="ae-empty">This company has no managers yet.</p>}
            {options && managers.length > 0 && (
              <div className="ae-manager-list" role="group" aria-labelledby={`${idPrefix}-managers-label`}>
                {visibleManagers.map((m) => {
                  const active = isActiveStatus(m.status);
                  const inputId = `${idPrefix}-manager-${m._id}`;
                  return (
                    <div key={m._id} className="form-check ae-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={inputId}
                        checked={selected.has(m._id)}
                        disabled={readOnly}
                        onChange={(e) => toggleManager(m._id, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={inputId}>
                        {m.name}
                        <span className="ae-manager-email"> {m.email}</span>
                        {!active && <span className="ae-manager-inactive"> (inactive, not emailed)</span>}
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <RecipientsEditor
            idPrefix={idPrefix}
            recipients={value.extraRecipients}
            readOnly={readOnly}
            onChange={(extraRecipients) => onChange({ extraRecipients })}
          />
        </>
      )}

      {summary ? (
        <p className="ae-note" aria-live="polite">{summary}</p>
      ) : (
        <p className="ae-off-notice" aria-live="polite">
          Nobody is chosen, so incident reports will not be emailed to anyone on the team. Tick the company account, a manager or add an address.
        </p>
      )}
      <p className="ae-help">
        The person who reported the incident still gets their confirmation, and addresses typed on a report are still emailed.
      </p>
    </>
  );
};

IncidentReportRecipientsFields.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  value: PropTypes.shape({
    customRecipients: PropTypes.bool,
    includeCompany: PropTypes.bool,
    managerIds: PropTypes.arrayOf(PropTypes.string),
    extraRecipients: PropTypes.arrayOf(recipientPropType),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  options: PropTypes.shape({
    company: PropTypes.shape({ name: PropTypes.string, email: PropTypes.string }),
    managers: PropTypes.arrayOf(
      PropTypes.shape({ _id: PropTypes.string, name: PropTypes.string, email: PropTypes.string, status: PropTypes.string })
    ),
  }),
  optionsError: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AutomatedEmailsSettings = ({ sections, readOnly, compact }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingKind, setSendingKind] = useState("");
  const [reminderTier, setReminderTier] = useState("reminder");
  const [incidentOptions, setIncidentOptions] = useState(null);
  const [incidentOptionsError, setIncidentOptionsError] = useState("");

  const visibleSections = useMemo(
    () => (sections || ALL_SECTIONS).filter((key) => ALL_SECTIONS.includes(key)),
    [sections]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await getAutomatedEmailSettings();
      const normalized = normalizeSettings(response?.data);
      setSaved(normalized);
      setDraft(normalized);
    } catch (error) {
      setLoadError(error.message || "Could not load the settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The manager list is for the company login only (the endpoint refuses others).
  const showsIncidentReports = visibleSections.includes("incidentReports");
  useEffect(() => {
    if (!showsIncidentReports || readOnly) return undefined;
    let cancelled = false;
    getIncidentRecipientOptions()
      .then((response) => {
        if (!cancelled) setIncidentOptions(response?.data || { company: {}, managers: [] });
      })
      .catch((error) => {
        if (!cancelled) setIncidentOptionsError(error.message || "Could not load the managers.");
      });
    return () => {
      cancelled = true;
    };
  }, [showsIncidentReports, readOnly]);

  const dirty = useMemo(() => {
    if (!draft || !saved) return false;
    const a = JSON.stringify(pickSections(normalizeSettings(draft), visibleSections));
    const b = JSON.stringify(pickSections(saved, visibleSections));
    return a !== b;
  }, [draft, saved, visibleSections]);

  // Warn before the tab closes with unsaved edits.
  useEffect(() => {
    if (!dirty || readOnly) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, readOnly]);

  const updateSection = useCallback((key, patch) => {
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const handleSave = async () => {
    if (!dirty || saving || readOnly) return;
    setSaving(true);
    try {
      const payload = pickSections(normalizeSettings(draft), visibleSections);
      const response = await saveAutomatedEmailSettings(payload);
      const normalized = normalizeSettings(response?.data);
      setSaved(normalized);
      setDraft(normalized);
      toast.success("Settings saved");
    } catch (error) {
      toast.error(error.message || "Could not save the settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = async () => {
    if (!dirty || saving) return;
    const result = await Swal.fire({
      title: "Discard changes?",
      text: "Anything you changed since the last save will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Discard",
      cancelButtonText: "Keep editing",
      confirmButtonColor: "#fd625e",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      setDraft(saved);
    }
  };

  const handleSendSample = async (kind, tier) => {
    if (sendingKind) return;
    setSendingKind(kind);
    try {
      const response = await sendAutomatedEmailTest({ kind, tier });
      toast.success(response?.message || "Sample sent. Check your inbox.");
    } catch (error) {
      toast.error(error.message || "Could not send the sample.");
    } finally {
      setSendingKind("");
    }
  };

  const rootClass = `ae-settings${compact ? " ae-compact" : ""}`;

  if (loading) {
    return (
      <div className={rootClass}>
        <Card className="ae-card">
          <CardBody className="ae-state" aria-busy="true">
            <Spinner color="primary" />
            <p>Loading automated email settings...</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (loadError || !draft) {
    return (
      <div className={rootClass}>
        <Card className="ae-card">
          <CardBody className="ae-state" role="alert">
            <FeatherIcon icon="alert-circle" size={28} color="#fd625e" />
            <p>
              <strong>We could not load the automated email settings.</strong>
            </p>
            <p>{loadError || "Something went wrong."}</p>
            <Button
              type="button"
              outline
              color="primary"
              className="ae-btn"
              onClick={load}
            >
              <FeatherIcon icon="refresh-cw" size={14} />
              Try again
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const renderSection = (key) => {
    const meta = SECTION_META[key];
    const value = draft[key];
    const idPrefix = `ae-${key}`;

    if (key === "incidentReports") {
      return (
        <Card key={key} className="ae-card">
          <CardBody>
            <div className="ae-card-header">
              <div>
                <h5 className="ae-title">{meta.title}</h5>
                <p className="ae-description">{meta.description}</p>
              </div>
            </div>
            <div className="ae-section-body">
              <IncidentReportRecipientsFields
                idPrefix={idPrefix}
                value={value}
                readOnly={readOnly}
                options={incidentOptions}
                optionsError={incidentOptionsError}
                onChange={(patch) => updateSection(key, patch)}
              />
            </div>
          </CardBody>
        </Card>
      );
    }
    const canSample = !readOnly && !!meta.testKind;
    const isSending = sendingKind === meta.testKind;

    return (
      <Card key={key} className="ae-card">
        <CardBody>
          <div className="ae-card-header">
            <div>
              <h5 className="ae-title">{meta.title}</h5>
              <p className="ae-description">{meta.description}</p>
              {!value.enabled && (
                <p className="ae-off-notice">
                  Switched off. Nothing is sent until it is turned back on.
                </p>
              )}
            </div>
            <div className="form-check form-switch ae-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={`${idPrefix}-enabled`}
                checked={!!value.enabled}
                disabled={readOnly}
                onChange={(e) => updateSection(key, { enabled: e.target.checked })}
              />
              <label className="form-check-label" htmlFor={`${idPrefix}-enabled`}>
                Enabled
              </label>
            </div>
          </div>

          <div className="ae-section-body">
            {key === "inspectionReminders" && (
              <InspectionRemindersFields
                idPrefix={idPrefix}
                value={value}
                readOnly={readOnly}
                onChange={(patch) => updateSection(key, patch)}
              />
            )}

            {key === "inspectionWeeklyDigest" && (
              <WeekdayField
                id={`${idPrefix}-day`}
                label="Send every"
                value={value.dayOfWeek}
                readOnly={readOnly}
                onChange={(dayOfWeek) => updateSection(key, { dayOfWeek })}
              />
            )}

            {key === "maintenanceDigest" && (
              <MaintenanceDigestFields
                idPrefix={idPrefix}
                value={value}
                readOnly={readOnly}
                onChange={(patch) => updateSection(key, patch)}
              />
            )}

            {key === "buildingInspectionIssues" && (
              <DigestDaysField
                idPrefix={idPrefix}
                days={value.days}
                readOnly={readOnly}
                onChange={(days) => updateSection(key, { days })}
              />
            )}

            {key === "occupancyReport" && (
              <WeekdayField
                id={`${idPrefix}-day`}
                label="Send every"
                value={value.dayOfWeek}
                readOnly={readOnly}
                onChange={(dayOfWeek) => updateSection(key, { dayOfWeek })}
              />
            )}

            <p className="ae-note">{meta.note}</p>

            <RecipientsEditor
              idPrefix={idPrefix}
              recipients={value.extraRecipients}
              readOnly={readOnly}
              onChange={(extraRecipients) => updateSection(key, { extraRecipients })}
            />

            {canSample && (
              <div className="ae-sample">
                {key === "inspectionReminders" && (
                  <div>
                    <Label for={`${idPrefix}-tier`} className="ae-label">
                      Sample to send
                    </Label>
                    <Input
                      id={`${idPrefix}-tier`}
                      type="select"
                      className="ae-select"
                      value={reminderTier}
                      onChange={(e) => setReminderTier(e.target.value)}
                    >
                      {REMINDER_TIERS.map((tier) => (
                        <option key={tier.value} value={tier.value}>
                          {tier.label}
                        </option>
                      ))}
                    </Input>
                  </div>
                )}
                <Button
                  type="button"
                  outline
                  color="primary"
                  className="ae-btn"
                  disabled={!!sendingKind}
                  onClick={() =>
                    handleSendSample(
                      meta.testKind,
                      key === "inspectionReminders" ? reminderTier : undefined
                    )
                  }
                >
                  {isSending ? (
                    <Spinner size="sm" />
                  ) : (
                    <FeatherIcon icon="send" size={14} />
                  )}
                  {isSending ? "Sending..." : "Send me a sample"}
                </Button>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    );
  };

  const lastSaved = formatTimestamp(saved?.updatedAt);
  let statusText = "All changes saved.";
  if (dirty) statusText = "You have unsaved changes.";
  else if (lastSaved) statusText = `Last saved ${lastSaved}.`;

  return (
    <div className={rootClass}>
      {readOnly && (
        <p className="ae-readonly-notice">
          You can see how these emails are set up, but only the company login can
          change them.
        </p>
      )}

      {visibleSections.map(renderSection)}

      {!readOnly && (
        <div className="ae-savebar">
          <span
            className={`ae-savebar-status${dirty ? " is-dirty" : ""}`}
            aria-live="polite"
          >
            {statusText}
          </span>
          <Button
            type="button"
            outline
            color="secondary"
            className="ae-btn"
            disabled={!dirty || saving}
            onClick={handleDiscard}
          >
            Discard
          </Button>
          <Button
            type="button"
            color="primary"
            className="ae-btn ae-btn-primary"
            disabled={!dirty || saving}
            onClick={handleSave}
          >
            {saving && <Spinner size="sm" />}
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
};

AutomatedEmailsSettings.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.oneOf(ALL_SECTIONS)),
  readOnly: PropTypes.bool,
  compact: PropTypes.bool,
};

AutomatedEmailsSettings.defaultProps = {
  sections: ALL_SECTIONS,
  readOnly: false,
  compact: false,
};

export default AutomatedEmailsSettings;
